import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReviewQueueService from '../services/Service';
import '../../Dashboard/styles/Dashboard.css';
import '../styles/ReviewQueue.css';
import showToast from '../../../../Shared/Utils/ToastNotification';

const ReviewQueue = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lockingPatientId, setLockingPatientId] = useState(null);

  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchReports = async (pageNum = 1, query = '', status = '') => {
    setLoading(true);
    try {
      const res = await ReviewQueueService.getAdminReports({ page: pageNum, search: query, status: status });
      setReports(res.data.results?.results || res.data.results || res.data || []);
      setTotalCount(res.data.count || res.data.results?.total_count || 0);
      setNextPage(res.data.next || res.data.results?.next);
      setPrevPage(res.data.previous || res.data.results?.previous);
      setPage(pageNum);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(page, searchQuery, statusFilter);
  }, [page, searchQuery, statusFilter]);

  const handleSearch = () => {
    setPage(1);
    setSearchQuery(searchTerm.trim());
  };

  const handleReset = () => {
    setSearchTerm('');
    setSearchQuery('');
    setStatusFilter('');
    setPage(1);
  };

  const handleKeyDown = (e) => e.key === 'Enter' && handleSearch();

  const handleNext = () => nextPage && setPage((prev) => prev + 1);
  const handlePrev = () => prevPage && setPage((prev) => Math.max(prev - 1, 1));

  const handleApprove = async (id) => {
    try {
      await ReviewQueueService.approveAdminReport(id);
      showToast('success', 'Report Approved & Unlocked!');
      fetchReports(page, searchQuery, statusFilter);
    } catch (e) {
      showToast('error', 'Failed to approve report');
    }
  };

  const handleTogglePortalLock = async (report) => {
    if (!report.patient_id) {
      showToast('error', 'Patient ID not found for this report');
      return;
    }
    setLockingPatientId(report.patient_id);
    try {
      const newLockState = !report.portal_locked;
      await ReviewQueueService.togglePortalLock(report.patient_id, newLockState);
      setReports((prev) => prev.map((r) => (r.patient_id === report.patient_id ? { ...r, portal_locked: newLockState } : r)));
      showToast('success', `Portal ${newLockState ? 'locked' : 'unlocked'} for ${report.patient_email}`);
      fetchReports(page, searchQuery, statusFilter);
    } catch (err) {
      showToast('error', err?.response?.data?.detail || 'Failed to update portal lock');
    } finally {
      setLockingPatientId(null);
    }
  };

  return (
    <div className="review-queue-container">
      <div className="rq-header">
        <h1 className="rq-title">Human Review Queue</h1>
        <button className="rq-btn-blue" onClick={() => fetchReports(page, searchQuery, statusFilter)}>
          Refresh
        </button>
      </div>

      {/* 🔍 Search Section */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{
            padding: '10px',
            borderRadius: '6px',
            border: '1px solid #334155',
            background: '#1e293b',
            color: '#f8fafc',
            flex: 1,
            minWidth: '200px',
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          style={{ padding: '10px', borderRadius: '6px', border: '1px solid #334155', background: '#1e293b', color: '#f8fafc', width: '250px' }}
        >
          <option value="">All Statuses</option>
          <option value="UNLOCKED">UNLOCKED</option>
          <option value="UNLOCK_APPROVED">UNLOCK APPROVED</option>
          <option value="HUMAN_REVIEW_REQUIRED">HUMAN REVIEW REQUIRED</option>
        </select>
        <button className="rq-btn-blue" onClick={handleSearch}>
          Search
        </button>
        <button className="rq-btn-outline" onClick={handleReset}>
          Reset
        </button>
      </div>

      {loading ? (
        <div style={{ color: '#cbd5e1' }}>Loading reports...</div>
      ) : (
        <div className="rq-table-wrapper">
          <table className="rq-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>PATIENT EMAIL</th>
                <th>TEST INFO</th>
                <th>STATUS</th>
                <th>SAFETY REVIEW</th>
                <th>CREATED AT</th>
                <th>APPROVE / ACTION</th>
                <th>UNLOCK PORTAL</th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {reports.length > 0 ? (
                reports.map((report) => {
                  const dateObj = new Date(report.created_at);
                  const dateStr = dateObj.toLocaleDateString('en-GB'); // dd/mm/yyyy
                  const timeStr = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

                  const isUnlocked = report.status === 'unlocked';
                  const rowAccentClass = isUnlocked ? 'accent-green' : 'accent-yellow';
                  const badgeClass = isUnlocked ? 'rq-badge-green' : 'rq-badge-yellow';

                  return (
                    <tr key={report.id} className={`rq-row ${rowAccentClass}`}>
                      <td>{report.id}</td>
                      <td>{report.patient_email || report.order}</td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ fontWeight: '500' }}>{report.ordered_tests || 'Unknown'}</span>
                          <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Reports: {report.info_status || '0/0'}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`rq-badge ${badgeClass}`}>{report.status.toUpperCase()}</span>
                      </td>
                      <td>
                        {report.human_review_required ? (
                          <span className="rq-safety-pending">Pending Check</span>
                        ) : report.unlock_approved ? (
                          <span className="rq-safety-approved">✓ Approved</span>
                        ) : (
                          <span className="rq-safety-pending">Pending Check</span>
                        )}
                      </td>
                      <td>
                        <div className="rq-date">
                          <div>{dateStr}</div>
                          <div className="rq-date-time">{timeStr}</div>
                        </div>
                      </td>
                      <td>
                        <button
                          className={report.unlock_approved ? 'rq-btn-dark' : 'rq-btn-blue'}
                          onClick={() => handleApprove(report.id)}
                          disabled={report.unlock_approved}
                        >
                          {report.unlock_approved ? 'Approved' : 'Approve & Unlock'}
                        </button>
                      </td>
                      <td>
                        {!report.unlock_approved ? (
                          <span style={{ color: '#94a3b8' }}>-</span>
                        ) : (
                          <button
                            className={report.portal_locked ? 'rq-btn-unlock-portal' : 'rq-btn-lock-portal'}
                            onClick={() => handleTogglePortalLock(report)}
                            disabled={lockingPatientId === report.patient_id}
                            title={report.portal_locked ? 'Unlock Portal' : 'Lock Portal'}
                          >
                            {lockingPatientId === report.patient_id ? '...' : report.portal_locked ? 'Unlock Portal' : ' Lock Portal'}
                          </button>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <button className="rq-btn-outline" onClick={() => navigate(`/admin/review-queue/${report.id}`)}>
                            Review
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '32px' }}>
                    No reports pending review.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', color: '#94a3b8' }}>
          <p>
            Total Reports: <strong style={{ color: '#fff' }}>{totalCount}</strong> | Page: <strong style={{ color: '#fff' }}>{page}</strong>
          </p>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="rq-btn-outline" disabled={!prevPage} onClick={handlePrev}>
              Previous
            </button>
            <button className="rq-btn-outline" disabled={!nextPage} onClick={handleNext}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewQueue;
